/**
 * Shared Firestore read helpers for the public site.
 *
 * Read strategy: each collection is fetched with a single
 * `where("status", "==", "published")` query and then filtered, sorted, searched
 * and paginated in memory by the existing pure helpers in /src/utilities.
 *
 * Two reasons, not just convenience:
 *  1. The catalogue search does substring matching across several fields
 *     (see applyProductFilters); Firestore has no native substring/full-text
 *     query, so that filtering has to happen client-side regardless.
 *  2. firestore.rules only permits a public `list` that is provably restricted
 *     to published docs — the equality filter is what makes the read legal.
 *
 * This is sized for the current catalogue (~12 products, ~8 posts). If a
 * collection grows into the hundreds, push sorting/pagination into Firestore
 * and add the matching composite indexes to firestore.indexes.json.
 */
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { db } from "@/lib/firebase";

/** Long enough to absorb a burst of navigation, short enough that an admin edit
 *  shows up without a hard reload. */
const CACHE_TTL_MS = 30_000;

const cache = new Map();

const isTimestamp = (value) =>
    value !== null && typeof value === "object" && typeof value.toDate === "function";

/**
 * Firestore hands back Timestamp objects, but every consumer in the UI treats
 * these fields as ISO strings (`new Date(post.publishedAt)`), so convert at the
 * boundary and keep the rest of the app unaware that Firestore is underneath.
 */
function normalize(value) {
    if (isTimestamp(value)) return value.toDate().toISOString();
    if (Array.isArray(value)) return value.map(normalize);
    if (value !== null && typeof value === "object") {
        const out = {};
        for (const [k, v] of Object.entries(value)) out[k] = normalize(v);
        return out;
    }
    return value;
}

/** Firestore doc -> plain entity. The doc ID is the identity, surfaced as `id`
 *  because that is what the components and mock fixtures already use. */
export function mapDoc(snapshot) {
    return { id: snapshot.id, ...normalize(snapshot.data()) };
}

async function cached(key, loader) {
    const hit = cache.get(key);
    if (hit && Date.now() - hit.at < CACHE_TTL_MS) return hit.promise;

    const promise = loader().catch((err) => {
        // Never cache a rejection, or one transient network blip poisons reads
        // for the whole TTL.
        cache.delete(key);
        throw err;
    });
    cache.set(key, { at: Date.now(), promise });
    return promise;
}

/** Drop cached reads. Called after admin writes so the panel reflects changes
 *  immediately rather than serving its own stale list back. */
export function invalidateCache(collectionName) {
    if (!collectionName) cache.clear();
    else for (const key of cache.keys()) if (key.startsWith(collectionName)) cache.delete(key);
}

/** All published docs in a collection. */
export async function getPublished(collectionName) {
    return cached(`${collectionName}:published`, async () => {
        const snap = await getDocs(
            query(collection(db, collectionName), where("status", "==", "published")),
        );
        return snap.docs.map(mapDoc);
    });
}

/** Every doc in a collection, drafts included. Admin-only: firestore.rules
 *  rejects this for anyone without the admin claim. */
export async function getAll(collectionName) {
    const snap = await getDocs(collection(db, collectionName));
    return snap.docs.map(mapDoc);
}

/** Single doc by ID, or null when absent. */
export async function getById(collectionName, id) {
    const snap = await getDoc(doc(db, collectionName, id));
    return snap.exists() ? mapDoc(snap) : null;
}

/** getById behind the TTL cache. For docs read on every page view — the
 *  settings singleton and the per-page SEO entries — where an uncached read
 *  would bill once per navigation. */
export async function getByIdCached(collectionName, id) {
    return cached(`${collectionName}:doc:${id}`, () => getById(collectionName, id));
}

/** Published doc matched on its `slug` field. Resolved from the cached
 *  published list rather than a second query — the list is already in memory,
 *  so this costs no extra reads. */
export async function getPublishedBySlug(collectionName, slug) {
    const items = await getPublished(collectionName);
    return items.find((item) => item.slug === slug) || null;
}
